<template>
  <div class="p-2 sm:p-4 text-lexend overflow-x-hidden min-h-screen bg-gray-50 dark:bg-gray-900/50">
    <!-- Loading -->
    <div v-if="!devolucion" class="flex items-center justify-center min-h-[60vh]">
      <div class="text-center">
        <div class="inline-block animate-spin rounded-full h-10 w-10 border-4 border-indigo-500 border-t-transparent"></div>
        <p class="mt-3 text-gray-500 dark:text-gray-400 font-bold text-sm">Cargando devolución...</p>
      </div>
    </div>

    <div v-else class="max-w-7xl mx-auto">
      <!-- Header -->
      <div class="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3 sm:gap-4 mb-3 sm:mb-5 mt-1 animate-fadeIn">
        <div class="flex items-start gap-3 w-full sm:w-auto">
          <button @click="$router.back()" class="p-2 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl hover:bg-gray-50 dark:hover:bg-gray-700 transition-all shadow-sm flex-shrink-0">
            <Icon icon="mdi:arrow-left" class="text-lg text-gray-600 dark:text-gray-300" />
          </button>
          <div class="flex-1 min-w-0">
            <div class="flex items-center gap-2 mb-1 flex-wrap">
              <div class="w-8 h-1 bg-indigo-600 rounded-full"></div>
              <span class="text-[10px] sm:text-xs font-black text-indigo-600 tracking-widest">Detalle de Devolución</span>
            </div>
            <h1 class="text-xl sm:text-3xl font-black text-gray-900 dark:text-white tracking-tight flex items-center flex-wrap gap-2">
              {{ devolucion.numero_devolucion }}
              <span class="badge-estado" :class="`estado-${devolucion.estado}`">
                {{ devolucion.estado }}
              </span>
            </h1>
            <p class="text-[10px] sm:text-xs text-gray-500 dark:text-gray-400 mt-0.5">{{ formatDate(devolucion.fecha_devolucion) }}</p>
          </div>
        </div>

        <div class="flex flex-wrap items-center gap-2 w-full sm:w-auto justify-end">
          <button v-if="devolucion.estado === 'pendiente'" @click="aprobar" class="px-3 sm:px-4 py-2 bg-gradient-to-r from-green-500 to-emerald-500 text-white rounded-xl font-black text-[10px] sm:text-xs tracking-widest transition-all shadow-lg shadow-green-200 dark:shadow-none flex items-center gap-2 hover:shadow-xl">
            <Icon icon="mdi:check-circle-outline" class="text-base sm:text-lg" />
            Aprobar
          </button>
          <button v-if="devolucion.estado === 'pendiente'" @click="abrirModalRechazo" class="px-3 sm:px-4 py-2 bg-gradient-to-r from-red-500 to-rose-500 text-white rounded-xl font-black text-[10px] sm:text-xs tracking-widest transition-all shadow-lg shadow-red-200 dark:shadow-none flex items-center gap-2 hover:shadow-xl">
            <Icon icon="mdi:close-circle-outline" class="text-base sm:text-lg" />
            Rechazar
          </button>
          <button v-if="['aprobada', 'parcial'].includes(devolucion.estado)" @click="abrirModalPago" class="px-3 sm:px-4 py-2 bg-gradient-to-r from-blue-500 to-indigo-500 text-white rounded-xl font-black text-[10px] sm:text-xs tracking-widest transition-all shadow-lg shadow-blue-200 dark:shadow-none flex items-center gap-2 hover:shadow-xl">
            <Icon icon="mdi:cash-plus" class="text-base sm:text-lg" />
            Registrar Pago
          </button>
          <button v-if="!['anulada', 'completada'].includes(devolucion.estado)" @click="abrirModalAnular" class="px-3 sm:px-4 py-2 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-300 rounded-xl font-black text-[10px] sm:text-xs tracking-widest transition-all shadow-sm flex items-center gap-2 hover:bg-gray-50 dark:hover:bg-gray-700">
            <Icon icon="mdi:cancel" class="text-base sm:text-lg" />
            Anular
          </button>
        </div>
      </div>

      <!-- Info principal -->
      <div class="grid grid-cols-1 lg:grid-cols-2 gap-3 sm:gap-4 mb-3 sm:mb-4 animate-fadeInUp">
        <!-- Información General -->
        <div class="bg-white dark:bg-gray-800 rounded-2xl shadow-md border border-gray-200 dark:border-gray-700 overflow-hidden">
          <div class="bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-gray-800 dark:to-gray-800 px-4 py-3 border-b border-gray-100 dark:border-gray-700 flex items-center gap-2">
            <div class="p-1.5 bg-blue-100 dark:bg-blue-900/30 rounded-lg">
              <Icon icon="mdi:file-document-outline" class="text-blue-600 dark:text-blue-400 text-lg" />
            </div>
            <h3 class="text-sm font-bold text-gray-800 dark:text-white uppercase tracking-wider">Información General</h3>
          </div>
          <div class="p-4 space-y-2.5">
            <div class="flex justify-between items-center text-sm">
              <span class="text-gray-500 dark:text-gray-400 font-bold text-xs uppercase">Tipo Origen</span>
              <span class="px-2.5 py-1 text-[10px] rounded-full font-black uppercase bg-indigo-100 text-indigo-700 dark:bg-indigo-900/30 dark:text-indigo-300">{{ formatTipoOrigen(devolucion.tipo_origen) }}</span>
            </div>
            <div class="flex justify-between items-center text-sm">
              <span class="text-gray-500 dark:text-gray-400 font-bold text-xs uppercase">Motivo</span>
              <strong class="text-gray-900 dark:text-white text-xs">{{ formatMotivo(devolucion.motivo) }}</strong>
            </div>
            <div class="flex justify-between items-center text-sm">
              <span class="text-gray-500 dark:text-gray-400 font-bold text-xs uppercase">Cliente</span>
              <strong class="text-gray-900 dark:text-white text-xs">{{ devolucion.cliente_nombre || '—' }}</strong>
            </div>
            <div v-if="devolucion.proveedor_nombre" class="flex justify-between items-center text-sm">
              <span class="text-gray-500 dark:text-gray-400 font-bold text-xs uppercase">Proveedor</span>
              <strong class="text-gray-900 dark:text-white text-xs">{{ devolucion.proveedor_nombre }}</strong>
            </div>
            <div class="flex justify-between items-center text-sm">
              <span class="text-gray-500 dark:text-gray-400 font-bold text-xs uppercase">Registró</span>
              <strong class="text-gray-900 dark:text-white text-xs">{{ devolucion.usuario_registra_nombre || '—' }}</strong>
            </div>
            <div class="flex justify-between items-center text-sm">
              <span class="text-gray-500 dark:text-gray-400 font-bold text-xs uppercase">Fecha</span>
              <strong class="text-gray-900 dark:text-white text-xs">{{ formatDate(devolucion.fecha_devolucion) }}</strong>
            </div>
            <div class="flex justify-between items-center text-sm">
              <span class="text-gray-500 dark:text-gray-400 font-bold text-xs uppercase">Tasa Bs/USD</span>
              <strong class="text-gray-900 dark:text-white text-xs">Bs {{ formatMoney(devolucion.tasa_bs_usd) }}</strong>
            </div>
            <div v-if="devolucion.observaciones" class="pt-2 border-t border-gray-100 dark:border-gray-700">
              <span class="text-gray-500 dark:text-gray-400 font-bold text-xs uppercase block mb-1">Observaciones</span>
              <p class="text-xs text-gray-700 dark:text-gray-300 leading-relaxed">{{ devolucion.observaciones }}</p>
            </div>
          </div>
        </div>

        <!-- Montos -->
        <div class="bg-white dark:bg-gray-800 rounded-2xl shadow-md border border-gray-200 dark:border-gray-700 overflow-hidden">
          <div class="bg-gradient-to-r from-green-50 to-emerald-50 dark:from-gray-800 dark:to-gray-800 px-4 py-3 border-b border-gray-100 dark:border-gray-700 flex items-center gap-2">
            <div class="p-1.5 bg-green-100 dark:bg-green-900/30 rounded-lg">
              <Icon icon="mdi:cash-multiple" class="text-green-600 dark:text-green-400 text-lg" />
            </div>
            <h3 class="text-sm font-bold text-gray-800 dark:text-white uppercase tracking-wider">Montos</h3>
          </div>
          <div class="p-4">
            <div class="grid grid-cols-2 gap-2 mb-3">
              <div class="p-3 bg-gray-50 dark:bg-gray-700/30 rounded-xl">
                <p class="text-[9px] font-bold text-gray-400 uppercase mb-0.5">Subtotal USD</p>
                <p class="text-sm font-black text-gray-900 dark:text-white">${{ formatMoney(devolucion.subtotal_usd) }}</p>
              </div>
              <div class="p-3 bg-gray-50 dark:bg-gray-700/30 rounded-xl">
                <p class="text-[9px] font-bold text-gray-400 uppercase mb-0.5">IVA USD</p>
                <p class="text-sm font-black text-gray-900 dark:text-white">${{ formatMoney(devolucion.iva_usd) }}</p>
              </div>
            </div>
            <div class="p-3 bg-gradient-to-r from-indigo-50 to-blue-50 dark:from-indigo-900/20 dark:to-blue-900/20 rounded-xl border border-indigo-100 dark:border-indigo-800 mb-3">
              <div class="flex justify-between items-center mb-1">
                <span class="text-[10px] font-bold text-indigo-600 dark:text-indigo-400 uppercase">Total USD</span>
                <span class="text-lg font-black text-indigo-700 dark:text-indigo-300">${{ formatMoney(devolucion.total_usd) }}</span>
              </div>
              <div class="flex justify-between items-center">
                <span class="text-[10px] font-bold text-indigo-600 dark:text-indigo-400 uppercase">Total Bs</span>
                <span class="text-sm font-black text-indigo-700 dark:text-indigo-300">Bs {{ formatMoney(devolucion.total_bs) }}</span>
              </div>
            </div>
            <div class="grid grid-cols-2 gap-2">
              <div class="p-3 bg-emerald-50 dark:bg-emerald-900/20 rounded-xl border border-emerald-100 dark:border-emerald-800">
                <p class="text-[9px] font-bold text-emerald-600 dark:text-emerald-400 uppercase mb-0.5">Reembolsado</p>
                <p class="text-sm font-black text-emerald-700 dark:text-emerald-300">${{ formatMoney(devolucion.monto_reembolsado_usd) }}</p>
              </div>
              <div class="p-3 bg-red-50 dark:bg-red-900/20 rounded-xl border border-red-100 dark:border-red-800">
                <p class="text-[9px] font-bold text-red-600 dark:text-red-400 uppercase mb-0.5">Saldo Pend.</p>
                <p class="text-sm font-black text-red-700 dark:text-red-300">${{ formatMoney(devolucion.saldo_pendiente_usd) }}</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Detalles -->
      <div class="bg-white dark:bg-gray-800 rounded-2xl shadow-md border border-gray-200 dark:border-gray-700 overflow-hidden mb-3 sm:mb-4 animate-fadeInUp delay-100">
        <div class="bg-gradient-to-r from-orange-50 to-amber-50 dark:from-gray-800 dark:to-gray-800 px-4 py-3 border-b border-gray-100 dark:border-gray-700 flex items-center justify-between">
          <div class="flex items-center gap-2">
            <div class="p-1.5 bg-orange-100 dark:bg-orange-900/30 rounded-lg">
              <Icon icon="mdi:package-variant" class="text-orange-600 dark:text-orange-400 text-lg" />
            </div>
            <h3 class="text-sm font-bold text-gray-800 dark:text-white uppercase tracking-wider">Detalles de la Devolución</h3>
          </div>
          <span class="px-2 py-0.5 bg-orange-100 dark:bg-orange-900/30 text-orange-700 dark:text-orange-300 text-[10px] font-black rounded-lg">
            {{ devolucion.detalles?.length || 0 }} items
          </span>
        </div>
        <div class="overflow-x-auto custom-scrollbar">
          <table class="w-full text-left border-collapse">
            <thead>
              <tr class="bg-gray-50 dark:bg-gray-900/50 border-b border-gray-200 dark:border-gray-700">
                <th class="px-4 py-3 text-[10px] font-black text-gray-400 tracking-widest">#</th>
                <th class="px-4 py-3 text-[10px] font-black text-gray-400 tracking-widest">Tipo</th>
                <th class="px-4 py-3 text-[10px] font-black text-gray-400 tracking-widest">Descripción</th>
                <th class="px-4 py-3 text-[10px] font-black text-gray-400 tracking-widest text-center">Cant.</th>
                <th class="px-4 py-3 text-[10px] font-black text-gray-400 tracking-widest text-right">Precio USD</th>
                <th class="px-4 py-3 text-[10px] font-black text-gray-400 tracking-widest text-right">Total USD</th>
                <th class="px-4 py-3 text-[10px] font-black text-gray-400 tracking-widest text-center">Condición</th>
              </tr>
            </thead>
            <tbody class="divide-y divide-gray-100 dark:divide-gray-700">
              <tr v-for="(d, idx) in devolucion.detalles" :key="d.idDevolucionDetalle" class="hover:bg-gray-50 dark:hover:bg-gray-700/30 transition-colors">
                <td class="px-4 py-3">
                  <span class="w-7 h-7 inline-flex items-center justify-center bg-orange-100 dark:bg-orange-900/30 text-orange-700 dark:text-orange-300 rounded-lg font-black text-xs">{{ idx + 1 }}</span>
                </td>
                <td class="px-4 py-3">
                  <span class="px-2 py-0.5 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 text-[9px] font-black rounded uppercase">{{ d.tipo_item }}</span>
                </td>
                <td class="px-4 py-3 text-xs font-bold text-gray-900 dark:text-white">{{ d.producto_nombre || d.servicio_nombre || d.descripcion_manual || '—' }}</td>
                <td class="px-4 py-3 text-center text-xs font-black text-gray-900 dark:text-white">{{ d.cantidad }}</td>
                <td class="px-4 py-3 text-right text-xs font-bold text-gray-700 dark:text-gray-300">${{ formatMoney(d.precio_unitario_usd) }}</td>
                <td class="px-4 py-3 text-right text-xs font-black text-indigo-600 dark:text-indigo-400">${{ formatMoney(d.total_usd) }}</td>
                <td class="px-4 py-3 text-center">
                  <span class="px-2 py-0.5 text-[9px] font-black rounded uppercase" :class="getCondicionClass(d.condicion_producto)">
                    {{ d.condicion_producto || '—' }}
                  </span>
                </td>
              </tr>
              <tr v-if="!devolucion.detalles?.length">
                <td colspan="7" class="px-4 py-12 text-center">
                  <div class="flex flex-col items-center opacity-30">
                    <Icon icon="mdi:package-off" class="text-5xl mb-2" />
                    <p class="text-sm font-black tracking-widest uppercase">Sin items</p>
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <!-- Pagos -->
      <div class="bg-white dark:bg-gray-800 rounded-2xl shadow-md border border-gray-200 dark:border-gray-700 overflow-hidden mb-3 sm:mb-4 animate-fadeInUp delay-100">
        <div class="bg-gradient-to-r from-emerald-50 to-green-50 dark:from-gray-800 dark:to-gray-800 px-4 py-3 border-b border-gray-100 dark:border-gray-700 flex items-center justify-between">
          <div class="flex items-center gap-2">
            <div class="p-1.5 bg-emerald-100 dark:bg-emerald-900/30 rounded-lg">
              <Icon icon="mdi:cash-multiple" class="text-emerald-600 dark:text-emerald-400 text-lg" />
            </div>
            <h3 class="text-sm font-bold text-gray-800 dark:text-white uppercase tracking-wider">Pagos / Reembolsos</h3>
          </div>
          <span v-if="devolucion.pagos?.length" class="px-2 py-0.5 bg-emerald-100 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-300 text-[10px] font-black rounded-lg">
            {{ devolucion.pagos.length }}
          </span>
        </div>
        <div v-if="devolucion.pagos?.length" class="overflow-x-auto custom-scrollbar">
          <table class="w-full text-left border-collapse">
            <thead>
              <tr class="bg-gray-50 dark:bg-gray-900/50 border-b border-gray-200 dark:border-gray-700">
                <th class="px-4 py-3 text-[10px] font-black text-gray-400 tracking-widest">Recibo</th>
                <th class="px-4 py-3 text-[10px] font-black text-gray-400 tracking-widest">Fecha</th>
                <th class="px-4 py-3 text-[10px] font-black text-gray-400 tracking-widest text-right">Monto USD</th>
                <th class="px-4 py-3 text-[10px] font-black text-gray-400 tracking-widest">Método</th>
                <th class="px-4 py-3 text-[10px] font-black text-gray-400 tracking-widest text-center">Estado</th>
              </tr>
            </thead>
            <tbody class="divide-y divide-gray-100 dark:divide-gray-700">
              <tr v-for="p in devolucion.pagos" :key="p.idDevolucionPago" class="hover:bg-gray-50 dark:hover:bg-gray-700/30 transition-colors">
                <td class="px-4 py-3 text-xs font-black text-indigo-600 dark:text-indigo-400">{{ p.numero_recibo || '—' }}</td>
                <td class="px-4 py-3 text-xs font-bold text-gray-600 dark:text-gray-300">{{ formatDate(p.fecha_pago) }}</td>
                <td class="px-4 py-3 text-right text-sm font-black text-emerald-600 dark:text-emerald-400">${{ formatMoney(p.monto_usd) }}</td>
                <td class="px-4 py-3">
                  <span class="px-2 py-0.5 bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300 text-[9px] font-black rounded uppercase">{{ p.metodo_pago }}</span>
                </td>
                <td class="px-4 py-3 text-center">
                  <span class="badge-estado" :class="`estado-${p.estado}`">{{ p.estado }}</span>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
        <div v-else class="p-12 text-center">
          <Icon icon="mdi:cash-off" class="text-5xl text-gray-300 dark:text-gray-600 mx-auto mb-2" />
          <p class="text-xs font-black text-gray-400 uppercase tracking-widest">No hay pagos registrados</p>
        </div>
      </div>

      <!-- Historial -->
      <div class="bg-white dark:bg-gray-800 rounded-2xl shadow-md border border-gray-200 dark:border-gray-700 overflow-hidden animate-fadeInUp delay-100">
        <div class="bg-gradient-to-r from-purple-50 to-pink-50 dark:from-gray-800 dark:to-gray-800 px-4 py-3 border-b border-gray-100 dark:border-gray-700 flex items-center justify-between">
          <div class="flex items-center gap-2">
            <div class="p-1.5 bg-purple-100 dark:bg-purple-900/30 rounded-lg">
              <Icon icon="mdi:history" class="text-purple-600 dark:text-purple-400 text-lg" />
            </div>
            <h3 class="text-sm font-bold text-gray-800 dark:text-white uppercase tracking-wider">Historial de Cambios</h3>
          </div>
          <span v-if="historial.length" class="px-2 py-0.5 bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300 text-[10px] font-black rounded-lg">
            {{ historial.length }}
          </span>
        </div>
        <div v-if="historial.length" class="p-4">
          <div class="relative pl-6">
            <div class="absolute left-[7px] top-2 bottom-2 w-0.5 bg-gradient-to-b from-purple-300 via-purple-200 to-transparent dark:from-purple-700 dark:via-purple-800"></div>
            <div v-for="(h, idx) in historial" :key="h.idHistorial" class="relative mb-4 last:mb-0">
              <div class="absolute -left-[22px] top-2 w-3 h-3 rounded-full bg-purple-500 border-2 border-white dark:border-gray-800 shadow-md"></div>
              <div class="p-3 bg-gray-50 dark:bg-gray-700/30 rounded-xl border border-gray-100 dark:border-gray-700 hover:shadow-sm transition-all">
                <div class="flex justify-between items-start gap-2 mb-1">
                  <strong class="text-xs font-black text-gray-900 dark:text-white uppercase tracking-wider">{{ h.accion }}</strong>
                  <span class="text-[9px] font-bold text-gray-400 whitespace-nowrap">{{ formatDate(h.fecha_cambio) }}</span>
                </div>
                <p v-if="h.estado_anterior || h.estado_nuevo" class="text-[10px] font-bold text-gray-500 dark:text-gray-400 mb-1">
                  <span class="text-red-500">{{ h.estado_anterior || '—' }}</span>
                  <Icon icon="mdi:arrow-right" class="inline mx-1 text-xs" />
                  <span class="text-green-500">{{ h.estado_nuevo || '—' }}</span>
                </p>
                <p v-if="h.observaciones" class="text-[10px] text-gray-600 dark:text-gray-300 leading-relaxed">{{ h.observaciones }}</p>
                <small v-if="h.usuario_nombre" class="text-[9px] text-gray-400 font-bold flex items-center gap-1 mt-1">
                  <Icon icon="mdi:account" class="text-xs" /> {{ h.usuario_nombre }}
                </small>
              </div>
            </div>
          </div>
        </div>
        <div v-else class="p-12 text-center">
          <Icon icon="mdi:history" class="text-5xl text-gray-300 dark:text-gray-600 mx-auto mb-2" />
          <p class="text-xs font-black text-gray-400 uppercase tracking-widest">Sin historial</p>
        </div>
      </div>
    </div>

    <!-- ============================================ -->
    <!-- MODAL DE PAGO -->
    <!-- ============================================ -->
    <Teleport to="body">
      <div v-if="modalPago.visible" class="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center p-3 sm:p-4 z-[9999]" @click.self="cerrarModalPago">
        <div class="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl w-full max-w-md overflow-hidden animate-zoomIn max-h-[95vh] flex flex-col">
          <div class="bg-gradient-to-r from-green-600 to-emerald-600 px-4 sm:px-6 py-3 sm:py-4 flex items-center justify-between flex-shrink-0">
            <div class="flex items-center gap-2 sm:gap-3">
              <div class="p-1.5 sm:p-2 bg-white/20 rounded-xl">
                <Icon icon="mdi:cash-plus" class="text-white text-lg sm:text-xl" />
              </div>
              <div>
                <h3 class="text-sm sm:text-base font-bold text-white uppercase tracking-wider">Registrar Pago</h3>
                <p class="text-[8px] sm:text-[10px] text-green-100 font-bold">Registrar reembolso al cliente</p>
              </div>
            </div>
            <button @click="cerrarModalPago" class="p-1.5 bg-white/10 hover:bg-white/20 rounded-lg transition-all">
              <Icon icon="mdi:close" class="text-white text-lg sm:text-xl" />
            </button>
          </div>

          <div class="p-4 sm:p-6 overflow-y-auto custom-scrollbar">
            <div class="bg-gray-50 dark:bg-gray-700/30 p-3 rounded-xl border border-gray-100 dark:border-gray-700 mb-4">
              <div class="flex justify-between text-[9px] sm:text-[10px] font-bold text-gray-400 uppercase mb-1">
                <span>Saldo Pendiente</span>
                <span class="text-red-600 text-sm font-black">${{ formatMoney(devolucion?.saldo_pendiente_usd) }}</span>
              </div>
            </div>

            <div class="space-y-3">
              <div>
                <label class="block text-[9px] sm:text-[10px] font-bold text-gray-400 uppercase mb-1 ml-1">Monto USD *</label>
                <input v-model.number="modalPago.monto_usd" type="number" step="0.01" placeholder="0.00" class="w-full px-3 py-2 bg-gray-50 dark:bg-gray-700/50 border border-gray-200 dark:border-gray-600 rounded-lg text-sm focus:border-green-500 outline-none text-gray-900 dark:text-white" />
              </div>
              <div>
                <label class="block text-[9px] sm:text-[10px] font-bold text-gray-400 uppercase mb-1 ml-1">Monto Bs</label>
                <input v-model.number="modalPago.monto_bs" type="number" step="0.01" placeholder="0.00" class="w-full px-3 py-2 bg-gray-50 dark:bg-gray-700/50 border border-gray-200 dark:border-gray-600 rounded-lg text-sm focus:border-green-500 outline-none text-gray-900 dark:text-white" />
              </div>
              <div>
                <label class="block text-[9px] sm:text-[10px] font-bold text-gray-400 uppercase mb-1 ml-1">Tasa Bs/USD</label>
                <input v-model.number="modalPago.tasa_bs_usd" type="number" step="0.01" placeholder="0.00" class="w-full px-3 py-2 bg-gray-50 dark:bg-gray-700/50 border border-gray-200 dark:border-gray-600 rounded-lg text-sm focus:border-green-500 outline-none text-gray-900 dark:text-white" />
              </div>
              <div>
                <label class="block text-[9px] sm:text-[10px] font-bold text-gray-400 uppercase mb-1 ml-1">Método de Pago</label>
                <select v-model="modalPago.metodo_pago" class="w-full px-3 py-2 bg-gray-50 dark:bg-gray-700/50 border border-gray-200 dark:border-gray-600 rounded-lg text-sm focus:border-green-500 outline-none cursor-pointer text-gray-900 dark:text-white">
                  <option value="efectivo">💵 Efectivo</option>
                  <option value="transferencia">🏦 Transferencia</option>
                  <option value="pago_movil">📱 Pago Móvil</option>
                  <option value="zelle">💰 Zelle</option>
                  <option value="usdt">🪙 USDT</option>
                  <option value="credito_cuenta">💳 Crédito en Cuenta</option>
                  <option value="nota_credito">📄 Nota de Crédito</option>
                </select>
              </div>
              <div>
                <label class="block text-[9px] sm:text-[10px] font-bold text-gray-400 uppercase mb-1 ml-1">Referencia</label>
                <input v-model="modalPago.referencia" type="text" placeholder="Nro. referencia" class="w-full px-3 py-2 bg-gray-50 dark:bg-gray-700/50 border border-gray-200 dark:border-gray-600 rounded-lg text-sm focus:border-green-500 outline-none text-gray-900 dark:text-white" />
              </div>
            </div>
          </div>

          <div class="px-4 sm:px-6 py-3 sm:py-4 border-t border-gray-100 dark:border-gray-700 bg-gray-50/50 dark:bg-gray-800/50 flex flex-col sm:flex-row gap-2 flex-shrink-0">
            <button @click="cerrarModalPago" class="w-full sm:flex-1 px-4 py-2 text-[10px] sm:text-xs font-black text-gray-400 uppercase border border-gray-100 dark:border-gray-700 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-all">Cancelar</button>
            <button @click="guardarPago" :disabled="procesando" class="w-full sm:flex-1 px-4 py-2 bg-green-600 text-white rounded-lg text-[10px] sm:text-xs font-black uppercase shadow-md hover:bg-green-700 disabled:opacity-50 transition-all flex items-center justify-center gap-2">
              <Icon v-if="procesando" icon="mdi:loading" class="animate-spin" />
              {{ procesando ? 'Guardando...' : 'Registrar Pago' }}
            </button>
          </div>
        </div>
      </div>
    </Teleport>

    <!-- ============================================ -->
    <!-- MODAL DE RECHAZO -->
    <!-- ============================================ -->
    <Teleport to="body">
      <div v-if="modalRechazo.visible" class="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center p-3 sm:p-4 z-[10000]" @click.self="cerrarModalRechazo">
        <div class="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl w-full max-w-md overflow-hidden animate-zoomIn">
          <div class="bg-gradient-to-r from-red-600 to-rose-600 px-4 sm:px-6 py-3 sm:py-4 flex items-center justify-between">
            <div class="flex items-center gap-2 sm:gap-3">
              <div class="p-1.5 sm:p-2 bg-white/20 rounded-xl">
                <Icon icon="mdi:alert-circle" class="text-white text-lg sm:text-xl" />
              </div>
              <div>
                <h3 class="text-sm sm:text-base font-bold text-white uppercase tracking-wider">Rechazar Devolución</h3>
                <p class="text-[8px] sm:text-[10px] text-red-100 font-bold">Indica el motivo del rechazo</p>
              </div>
            </div>
            <button @click="cerrarModalRechazo" class="p-1.5 bg-white/10 hover:bg-white/20 rounded-lg transition-all">
              <Icon icon="mdi:close" class="text-white text-lg sm:text-xl" />
            </button>
          </div>
          <div class="p-4 sm:p-6">
            <label class="block text-[9px] sm:text-[10px] font-bold text-gray-400 uppercase mb-1 ml-1">Motivo del rechazo *</label>
            <textarea v-model="modalRechazo.motivo" rows="3" placeholder="Explica por qué se rechaza..." class="w-full px-3 py-2 bg-gray-50 dark:bg-gray-700/50 border border-gray-200 dark:border-gray-600 rounded-lg text-sm focus:border-red-500 outline-none text-gray-900 dark:text-white resize-none"></textarea>
          </div>
          <div class="px-4 sm:px-6 py-3 sm:py-4 border-t border-gray-100 dark:border-gray-700 bg-gray-50/50 dark:bg-gray-800/50 flex flex-col sm:flex-row gap-2">
            <button @click="cerrarModalRechazo" class="w-full sm:flex-1 px-4 py-2 text-[10px] sm:text-xs font-black text-gray-400 uppercase border border-gray-100 dark:border-gray-700 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-all">Cancelar</button>
            <button @click="confirmarRechazo" :disabled="procesando || !modalRechazo.motivo.trim()" class="w-full sm:flex-1 px-4 py-2 bg-red-600 text-white rounded-lg text-[10px] sm:text-xs font-black uppercase shadow-md hover:bg-red-700 disabled:opacity-50 transition-all flex items-center justify-center gap-2">
              <Icon v-if="procesando" icon="mdi:loading" class="animate-spin" />
              {{ procesando ? 'Rechazando...' : 'Confirmar Rechazo' }}
            </button>
          </div>
        </div>
      </div>
    </Teleport>

    <!-- ============================================ -->
    <!-- MODAL DE ANULACIÓN -->
    <!-- ============================================ -->
    <Teleport to="body">
      <div v-if="modalAnular.visible" class="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center p-3 sm:p-4 z-[10000]" @click.self="cerrarModalAnular">
        <div class="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl w-full max-w-md overflow-hidden animate-zoomIn">
          <div class="bg-gradient-to-r from-gray-700 to-gray-900 px-4 sm:px-6 py-3 sm:py-4 flex items-center justify-between">
            <div class="flex items-center gap-2 sm:gap-3">
              <div class="p-1.5 sm:p-2 bg-white/20 rounded-xl">
                <Icon icon="mdi:cancel" class="text-white text-lg sm:text-xl" />
              </div>
              <div>
                <h3 class="text-sm sm:text-base font-bold text-white uppercase tracking-wider">Anular Devolución</h3>
                <p class="text-[8px] sm:text-[10px] text-gray-300 font-bold">Esta acción no se puede deshacer</p>
              </div>
            </div>
            <button @click="cerrarModalAnular" class="p-1.5 bg-white/10 hover:bg-white/20 rounded-lg transition-all">
              <Icon icon="mdi:close" class="text-white text-lg sm:text-xl" />
            </button>
          </div>
          <div class="p-4 sm:p-6">
            <label class="block text-[9px] sm:text-[10px] font-bold text-gray-400 uppercase mb-1 ml-1">Motivo de anulación *</label>
            <textarea v-model="modalAnular.motivo" rows="3" placeholder="Explica por qué se anula..." class="w-full px-3 py-2 bg-gray-50 dark:bg-gray-700/50 border border-gray-200 dark:border-gray-600 rounded-lg text-sm focus:border-gray-500 outline-none text-gray-900 dark:text-white resize-none"></textarea>
          </div>
          <div class="px-4 sm:px-6 py-3 sm:py-4 border-t border-gray-100 dark:border-gray-700 bg-gray-50/50 dark:bg-gray-800/50 flex flex-col sm:flex-row gap-2">
            <button @click="cerrarModalAnular" class="w-full sm:flex-1 px-4 py-2 text-[10px] sm:text-xs font-black text-gray-400 uppercase border border-gray-100 dark:border-gray-700 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-all">Cancelar</button>
            <button @click="confirmarAnulacion" :disabled="procesando || !modalAnular.motivo.trim()" class="w-full sm:flex-1 px-4 py-2 bg-gray-800 text-white rounded-lg text-[10px] sm:text-xs font-black uppercase shadow-md hover:bg-gray-900 disabled:opacity-50 transition-all flex items-center justify-center gap-2">
              <Icon v-if="procesando" icon="mdi:loading" class="animate-spin" />
              {{ procesando ? 'Anulando...' : 'Confirmar Anulación' }}
            </button>
          </div>
        </div>
      </div>
    </Teleport>

    <!-- ============================================ -->
    <!-- MODAL DE CONFIRMACIÓN -->
    <!-- ============================================ -->
    <Teleport to="body">
      <div v-if="modalConfirm.show" class="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center p-3 sm:p-4 z-[10001]" @click.self="cerrarConfirm">
        <div class="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl w-full max-w-sm overflow-hidden animate-zoomIn">
          <div :class="['px-4 sm:px-6 py-3 sm:py-4 flex items-center justify-between', modalConfirm.headerClass]">
            <div class="flex items-center gap-2 sm:gap-3">
              <div class="p-1.5 sm:p-2 bg-white/20 rounded-xl">
                <Icon :icon="modalConfirm.icon" class="text-white text-lg sm:text-xl" />
              </div>
              <div>
                <h3 class="text-sm sm:text-base font-bold text-white uppercase tracking-wider">{{ modalConfirm.titulo }}</h3>
                <p :class="['text-[8px] sm:text-[10px] font-bold', modalConfirm.subtitleClass]">{{ modalConfirm.subtitulo }}</p>
              </div>
            </div>
            <button @click="cerrarConfirm" class="p-1.5 bg-white/10 hover:bg-white/20 rounded-lg transition-all">
              <Icon icon="mdi:close" class="text-white text-lg sm:text-xl" />
            </button>
          </div>
          <div class="p-4 sm:p-6">
            <div :class="['p-3 sm:p-4 rounded-xl border text-center mb-4', modalConfirm.boxClass]">
              <p :class="['text-[9px] sm:text-[10px] font-bold uppercase mb-1', modalConfirm.boxTitleClass]">{{ modalConfirm.mensaje }}</p>
              <p class="text-sm sm:text-base font-black text-gray-800 dark:text-white">{{ modalConfirm.detalle }}</p>
            </div>
            <div class="flex flex-col sm:flex-row gap-2">
              <button @click="cerrarConfirm" class="w-full sm:flex-1 px-4 py-2 text-[10px] sm:text-xs font-black text-gray-400 uppercase border border-gray-100 dark:border-gray-700 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-all">Cancelar</button>
              <button @click="confirmarAccion" :disabled="procesando" :class="['w-full sm:flex-1 px-4 py-2 text-white rounded-lg text-[10px] sm:text-xs font-black uppercase shadow-md transition-all flex items-center justify-center gap-2', modalConfirm.btnClass]">
                <Icon v-if="procesando" icon="mdi:loading" class="animate-spin" />
                {{ procesando ? 'Procesando...' : modalConfirm.confirmText }}
              </button>
            </div>
          </div>
        </div>
      </div>
    </Teleport>

    <!-- ============================================ -->
    <!-- TOASTS PROFESIONALES -->
    <!-- ============================================ -->
    <Teleport to="body">
      <div class="fixed top-4 right-4 z-[999999] flex flex-col gap-3 pointer-events-none max-w-sm w-full px-2">
        <TransitionGroup
          enter-active-class="transition-all duration-300 ease-out"
          enter-from-class="opacity-0 translate-x-full scale-95"
          enter-to-class="opacity-100 translate-x-0 scale-100"
          leave-active-class="transition-all duration-300 ease-in absolute"
          leave-from-class="opacity-100 translate-x-0 scale-100"
          leave-to-class="opacity-0 translate-x-full scale-95"
          move-class="transition-all duration-300"
        >
          <div
            v-for="toast in toasts"
            :key="toast.id"
            class="pointer-events-auto relative overflow-hidden rounded-2xl shadow-2xl backdrop-blur-xl border w-full"
            :class="getToastClasses(toast.type)"
          >
            <div class="absolute bottom-0 left-0 h-1 rounded-full" :class="getToastProgressClass(toast.type)" :style="{ animation: `toastProgress ${toast.duration}ms linear forwards` }"></div>
            <div class="flex items-start gap-3 p-4">
              <div class="flex-shrink-0 w-10 h-10 rounded-xl flex items-center justify-center shadow-sm" :class="getToastIconBg(toast.type)">
                <Icon :icon="getToastIcon(toast.type)" class="text-2xl animate-toastIcon" :class="getToastIconColor(toast.type)" />
              </div>
              <div class="flex-1 min-w-0">
                <p class="font-bold text-sm" :class="getToastTitleColor(toast.type)">{{ toast.title }}</p>
                <p v-if="toast.message" class="text-xs mt-0.5 leading-relaxed" :class="getToastMessageColor(toast.type)">{{ toast.message }}</p>
              </div>
              <button @click="cerrarToast(toast.id)" class="flex-shrink-0 p-1 rounded-lg transition-all hover:scale-110" :class="getToastCloseClass(toast.type)">
                <Icon icon="mdi:close" class="text-base" />
              </button>
            </div>
          </div>
        </TransitionGroup>
      </div>
    </Teleport>
  </div>
</template>

<script setup>
import { ref, onMounted } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { Icon } from '@iconify/vue';
import { getCurrentUser } from '@/api/index';
import {
  obtenerDevolucionPorId,
  obtenerHistorialDevolucion,
  aprobarDevolucion,
  rechazarDevolucion,
  anularDevolucionConMotivo,
  registrarPagoDevolucion
} from '@/api/devoluciones';

const route = useRoute();
const router = useRouter();
const usuario = getCurrentUser();
const id = route.params.id;

const devolucion = ref(null);
const historial = ref([]);
const procesando = ref(false);

// ⭐ TOASTS
const toasts = ref([]);
let toastIdCounter = 0;

const mostrarToast = (type, title, message = '', duration = 4000) => {
  const toastId = ++toastIdCounter;
  toasts.value.push({ id: toastId, type, title, message, duration });
  setTimeout(() => cerrarToast(toastId), duration);
};
const cerrarToast = (tid) => {
  const idx = toasts.value.findIndex(t => t.id === tid);
  if (idx !== -1) toasts.value.splice(idx, 1);
};
const toastSuccess = (t, m = '') => mostrarToast('success', t, m, 4000);
const toastError = (t, m = '') => mostrarToast('error', t, m, 5000);
const toastWarning = (t, m = '') => mostrarToast('warning', t, m, 4500);
const toastInfo = (t, m = '') => mostrarToast('info', t, m, 4000);

const getToastClasses = (type) => ({
  success: 'bg-white/95 dark:bg-gray-800/95 border-emerald-200 dark:border-emerald-800/60',
  error: 'bg-white/95 dark:bg-gray-800/95 border-red-200 dark:border-red-800/60',
  warning: 'bg-white/95 dark:bg-gray-800/95 border-amber-200 dark:border-amber-800/60',
  info: 'bg-white/95 dark:bg-gray-800/95 border-blue-200 dark:border-blue-800/60'
}[type] || '');

const getToastProgressClass = (type) => ({
  success: 'bg-gradient-to-r from-emerald-400 to-green-500',
  error: 'bg-gradient-to-r from-red-400 to-rose-500',
  warning: 'bg-gradient-to-r from-amber-400 to-orange-500',
  info: 'bg-gradient-to-r from-blue-400 to-indigo-500'
}[type] || '');

const getToastIconBg = (type) => ({
  success: 'bg-gradient-to-br from-emerald-100 to-green-100 dark:from-emerald-900/40 dark:to-green-900/40',
  error: 'bg-gradient-to-br from-red-100 to-rose-100 dark:from-red-900/40 dark:to-rose-900/40',
  warning: 'bg-gradient-to-br from-amber-100 to-orange-100 dark:from-amber-900/40 dark:to-orange-900/40',
  info: 'bg-gradient-to-br from-blue-100 to-indigo-100 dark:from-blue-900/40 dark:to-indigo-900/40'
}[type] || '');

const getToastIcon = (type) => ({
  success: 'mdi:check-circle',
  error: 'mdi:alert-circle',
  warning: 'mdi:alert',
  info: 'mdi:information'
}[type] || 'mdi:information');

const getToastIconColor = (type) => ({
  success: 'text-emerald-600 dark:text-emerald-400',
  error: 'text-red-600 dark:text-red-400',
  warning: 'text-amber-600 dark:text-amber-400',
  info: 'text-blue-600 dark:text-blue-400'
}[type] || '');

const getToastTitleColor = (type) => ({
  success: 'text-emerald-900 dark:text-emerald-100',
  error: 'text-red-900 dark:text-red-100',
  warning: 'text-amber-900 dark:text-amber-100',
  info: 'text-blue-900 dark:text-blue-100'
}[type] || '');

const getToastMessageColor = (type) => ({
  success: 'text-emerald-700 dark:text-emerald-300',
  error: 'text-red-700 dark:text-red-300',
  warning: 'text-amber-700 dark:text-amber-300',
  info: 'text-blue-700 dark:text-blue-300'
}[type] || '');

const getToastCloseClass = (type) => ({
  success: 'text-emerald-500 hover:bg-emerald-100 dark:hover:bg-emerald-900/30',
  error: 'text-red-500 hover:bg-red-100 dark:hover:bg-red-900/30',
  warning: 'text-amber-500 hover:bg-amber-100 dark:hover:bg-amber-900/30',
  info: 'text-blue-500 hover:bg-blue-100 dark:hover:bg-blue-900/30'
}[type] || '');

// ⭐ MODALES
const modalPago = ref({
  visible: false,
  monto_usd: 0,
  monto_bs: 0,
  tasa_bs_usd: 0,
  metodo_pago: 'efectivo',
  referencia: ''
});

const modalRechazo = ref({ visible: false, motivo: '' });
const modalAnular = ref({ visible: false, motivo: '' });

const modalConfirm = ref({
  show: false,
  titulo: '',
  subtitulo: '',
  mensaje: '',
  detalle: '',
  icon: 'mdi:help-circle',
  headerClass: 'bg-gradient-to-r from-blue-600 to-indigo-600',
  subtitleClass: 'text-blue-100',
  boxClass: 'bg-blue-50/30 dark:bg-blue-900/10 border-blue-100 dark:border-blue-900/30',
  boxTitleClass: 'text-blue-700 dark:text-blue-400',
  btnClass: 'bg-blue-600 hover:bg-blue-700',
  confirmText: 'Confirmar',
  onConfirm: null
});

const abrirConfirm = ({ titulo, subtitulo = '', mensaje, detalle, icon, headerClass, subtitleClass, boxClass, boxTitleClass, btnClass, confirmText = 'Confirmar', onConfirm }) => {
  modalConfirm.value = { show: true, titulo, subtitulo, mensaje, detalle, icon, headerClass, subtitleClass, boxClass, boxTitleClass, btnClass, confirmText, onConfirm };
};
const cerrarConfirm = () => { modalConfirm.value.show = false; };
const confirmarAccion = async () => {
  if (modalConfirm.value.onConfirm) await modalConfirm.value.onConfirm();
  cerrarConfirm();
};

// =====================================================
// MÉTODOS
// =====================================================
const cargar = async () => {
  try {
    devolucion.value = await obtenerDevolucionPorId(id);
    historial.value = await obtenerHistorialDevolucion(id);
  } catch (e) {
    console.error(e);
    toastError('Error al cargar', 'No se pudo obtener la devolución.');
  }
};

const aprobar = () => {
  abrirConfirm({
    titulo: 'Aprobar Devolución',
    subtitulo: 'Confirmar aprobación',
    mensaje: '¿Aprobar esta devolución?',
    detalle: devolucion.value.numero_devolucion,
    icon: 'mdi:check-circle',
    headerClass: 'bg-gradient-to-r from-green-600 to-emerald-600',
    subtitleClass: 'text-green-100',
    boxClass: 'bg-green-50/30 dark:bg-green-900/10 border-green-100 dark:border-green-900/30',
    boxTitleClass: 'text-green-700 dark:text-green-400',
    btnClass: 'bg-green-600 hover:bg-green-700',
    confirmText: 'Sí, aprobar',
    onConfirm: async () => {
      procesando.value = true;
      try {
        await aprobarDevolucion(id, usuario?.idUsuario);
        await cargar();
        toastSuccess('Devolución aprobada', devolucion.value.numero_devolucion);
      } catch (e) {
        toastError('Error al aprobar', e.message || 'No se pudo aprobar.');
      } finally { procesando.value = false; }
    }
  });
};

const abrirModalRechazo = () => { modalRechazo.value = { visible: true, motivo: '' }; };
const cerrarModalRechazo = () => { modalRechazo.value.visible = false; };

const confirmarRechazo = async () => {
  if (!modalRechazo.value.motivo.trim()) {
    toastWarning('Motivo requerido', 'Debes explicar el motivo del rechazo.');
    return;
  }
  procesando.value = true;
  try {
    await rechazarDevolucion(id, usuario?.idUsuario, modalRechazo.value.motivo.trim());
    await cargar();
    toastWarning('Devolución rechazada', devolucion.value.numero_devolucion);
    cerrarModalRechazo();
  } catch (e) {
    toastError('Error al rechazar', e.message || 'No se pudo rechazar.');
  } finally { procesando.value = false; }
};

const abrirModalAnular = () => { modalAnular.value = { visible: true, motivo: '' }; };
const cerrarModalAnular = () => { modalAnular.value.visible = false; };

const confirmarAnulacion = async () => {
  if (!modalAnular.value.motivo.trim()) {
    toastWarning('Motivo requerido', 'Debes explicar el motivo de anulación.');
    return;
  }
  procesando.value = true;
  try {
    await anularDevolucionConMotivo(id, modalAnular.value.motivo.trim());
    await cargar();
    toastInfo('Devolución anulada', devolucion.value.numero_devolucion);
    cerrarModalAnular();
  } catch (e) {
    toastError('Error al anular', e.message || 'No se pudo anular.');
  } finally { procesando.value = false; }
};

const abrirModalPago = () => {
  modalPago.value = {
    visible: true,
    monto_usd: devolucion.value.saldo_pendiente_usd,
    monto_bs: devolucion.value.saldo_pendiente_bs || 0,
    tasa_bs_usd: devolucion.value.tasa_bs_usd,
    metodo_pago: 'efectivo',
    referencia: ''
  };
};
const cerrarModalPago = () => { modalPago.value.visible = false; };

const guardarPago = async () => {
  if (modalPago.value.monto_usd <= 0 && modalPago.value.monto_bs <= 0) {
    toastWarning('Monto inválido', 'El monto debe ser mayor a cero.');
    return;
  }
  procesando.value = true;
  try {
    await registrarPagoDevolucion({
      idDevolucion: parseInt(id),
      monto_usd: modalPago.value.monto_usd,
      monto_bs: modalPago.value.monto_bs,
      tasa_bs_usd: modalPago.value.tasa_bs_usd,
      metodo_pago: modalPago.value.metodo_pago,
      referencia: modalPago.value.referencia,
      idUsuario: usuario?.idUsuario
    });
    cerrarModalPago();
    await cargar();
    toastSuccess('Pago registrado', 'El reembolso se guardó correctamente.');
  } catch (e) {
    toastError('Error al registrar pago', e.message || 'No se pudo guardar.');
  } finally { procesando.value = false; }
};

// =====================================================
// FORMATEADORES
// =====================================================
const formatMoney = (v) => Number(v || 0).toFixed(2);
const formatDate = (d) => d ? new Date(d).toLocaleString('es-VE', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '—';

const formatTipoOrigen = (t) => ({
  venta: '🛒 Venta',
  servicio_externo: '🔧 Serv. Ext.',
  credito_salida_equipo: '📦 Crédito',
  prestamo: '💵 Préstamo',
  compra: '🏭 Compra',
  retencion: '📄 Retención'
}[t] || t || '—');

const formatMotivo = (m) => ({
  producto_defectuoso: 'Producto defectuoso',
  error_cobro: 'Error de cobro',
  servicio_no_realizado: 'Servicio no realizado',
  cliente_arrepentido: 'Cliente arrepentido',
  garantia: 'Garantía',
  sobrepago: 'Sobrepago',
  anulacion: 'Anulación',
  otro: 'Otro'
}[m] || m || '—');

const getCondicionClass = (c) => ({
  bueno: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400',
  defectuoso: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400',
  dañado: 'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400',
  inservible: 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300'
}[c] || 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300');

onMounted(cargar);
</script>

<style scoped>
.text-lexend { font-family: 'Lexend', sans-serif; }

.custom-scrollbar::-webkit-scrollbar { width: 6px; height: 6px; }
.custom-scrollbar::-webkit-scrollbar-track { background: transparent; }
.custom-scrollbar::-webkit-scrollbar-thumb { background: #e2e8f0; border-radius: 10px; }
.dark .custom-scrollbar::-webkit-scrollbar-thumb { background: #334155; }

.dark select option { background-color: #1f2937; color: #ffffff; }

@keyframes zoomIn { from { opacity: 0; transform: scale(0.95); } to { opacity: 1; transform: scale(1); } }
.animate-zoomIn { animation: zoomIn 0.2s ease-out; }

@keyframes fadeInUp { from { opacity: 0; transform: translateY(15px); } to { opacity: 1; transform: translateY(0); } }
.animate-fadeInUp { animation: fadeInUp 0.4s ease-out forwards; }
.delay-100 { animation-delay: 0.1s; }

@keyframes fadeIn { from { opacity: 0; } to { opacity: 1; } }
.animate-fadeIn { animation: fadeIn 0.3s ease-out; }

@keyframes toastProgress { from { width: 100%; } to { width: 0%; } }
@keyframes toastIconBounce {
  0% { transform: scale(0.5) rotate(-10deg); opacity: 0; }
  50% { transform: scale(1.15) rotate(5deg); opacity: 1; }
  100% { transform: scale(1) rotate(0deg); opacity: 1; }
}
.animate-toastIcon { animation: toastIconBounce 0.5s cubic-bezier(0.68, -0.55, 0.265, 1.55); }

/* Badges de estado */
.badge-estado {
  display: inline-block;
  padding: 4px 10px;
  border-radius: 12px;
  font-size: 10px;
  font-weight: 900;
  text-transform: uppercase;
  letter-spacing: 0.02em;
}
.estado-pendiente { background: #fff3cd; color: #856404; }
.dark .estado-pendiente { background: rgba(245, 158, 11, 0.2); color: #fcd34d; }
.estado-aprobada { background: #d1e7dd; color: #0a3622; }
.dark .estado-aprobada { background: rgba(16, 185, 129, 0.2); color: #6ee7b7; }
.estado-parcial { background: #cfe2ff; color: #084298; }
.dark .estado-parcial { background: rgba(59, 130, 246, 0.2); color: #93c5fd; }
.estado-completada { background: #d1f7dd; color: #0f5132; }
.dark .estado-completada { background: rgba(16, 185, 129, 0.25); color: #86efac; }
.estado-rechazada { background: #f8d7da; color: #842029; }
.dark .estado-rechazada { background: rgba(239, 68, 68, 0.2); color: #fca5a5; }
.estado-anulada { background: #e2e3e5; color: #41464b; }
.dark .estado-anulada { background: rgba(107, 114, 128, 0.3); color: #d1d5db; }
.estado-completado { background: #d1f7dd; color: #0f5132; }
.dark .estado-completado { background: rgba(16, 185, 129, 0.25); color: #86efac; }

html { scroll-behavior: smooth; }
</style>